import { GetServerSideProps, NextPage } from 'next'
import React, { memo } from 'react'
import { SearchResults } from '../../components/SearchResults/SearchResults'
import LayoutMain from '../../layout/LayoutMain'
import { redirectOnRewrite } from '../../utils/redirectOnRewrite'
import { useBackendQuery } from '@thirstycamel/ui'
import { CategoryEntity } from '@ts/core/src/modules/category/category.entity'
import { CategorySchemaResponse } from '@ts/core/src/modules/category/category.schema.objects'
import { useRouter } from 'next/router'
import { backend } from '../../utils/backend'
import { buildCategorySchema } from '../../utils/schema'

interface CategoryPageProps {
  categorySchemaData: CategorySchemaResponse | null
}

const Categories: NextPage<CategoryPageProps> = ({ categorySchemaData }) => {
  const router = useRouter()

  const [category] = [router.query.category] as string[]
  const [group] = [router.query.group] as string[]

  //To differentiate between category and group, check if group is defined (eg overlap between premix/vodka and spirits/vodka)
  const queryKey = group ? `categories/${category}?group=${group}` : `categories/${category}`
  const { data } = useBackendQuery<CategoryEntity>([queryKey])

  // Canonical URL: CategoryGroup > Fallback
  const canonicalUrl = categorySchemaData?.canonicalPath
    ? `https://www.thirstycamel.com.au${categorySchemaData.canonicalPath}`
    : data?.groups?.slug && data?.slug
    ? `https://www.thirstycamel.com.au/${data?.groups?.slug}/${data?.slug}`
    : `https://www.thirstycamel.com.au${router.asPath.split('?')[0]}`
  const structuredData = categorySchemaData?.items?.length
    ? buildCategorySchema(categorySchemaData, canonicalUrl)
    : undefined

  return (
    <LayoutMain
      title={data?.meta_title}
      meta_title={data?.meta_title}
      meta_description={data?.meta_description}
      noContainer
      canonicalUrl={canonicalUrl}
      structuredData={structuredData}
    >
      <SearchResults totals facetByCategory seo_description={data?.seo_description} />
    </LayoutMain>
  )
}

export const getServerSideProps: GetServerSideProps = async context => {
  await redirectOnRewrite(context)

  const categoryParam = context.params?.category
  const groupParam = context.params?.group

  const category = Array.isArray(categoryParam) ? categoryParam[0] : categoryParam
  const group = Array.isArray(groupParam) ? groupParam[0] : groupParam

  let categorySchemaData: CategorySchemaResponse | null = null

  if (typeof category === 'string' && typeof group === 'string') {
    categorySchemaData = await backend<CategorySchemaResponse>(`/categories/${category}/schema`, {
      params: { group },
    }).catch(() => null)
  }

  return { props: { categorySchemaData } }
}

export default memo(Categories)
